/**
 * Cricket team crests at `Cricket/Team Logos/{slug}.png`
 * (CloudFront → cricket8-web-content; same flat pattern as Football/Team Logos).
 */

import { DEFAULT_SPORT_IMAGES_CDN, sportImagePublicBase } from "./sportImageCdn";

const DEFAULT_CRICKET_TEAM_LOGO_S3_PREFIX = "Cricket/Team Logos";

/** Legacy CloudFront layout: `IPL+Team+Images/{Team}/{Team_Name}.png` */
const LEGACY_CDN_PREFIX = "IPL+Team+Images";

export const CRICKET_TEAM_LOGO_S3_BASE = sportImagePublicBase(
  DEFAULT_CRICKET_TEAM_LOGO_S3_PREFIX,
  process.env.CRICKET_TEAM_LOGO_BASE
);

export const CRICKET_TEAM_LOGO_DEFAULT_S3_FOLDER = "default";

export function slugifyCricketTeamName(name: string): string {
  return String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** Default shield when no team crest is found. */
export function resolveCricketDefaultShieldUrl(fallbackOverride: string = "false"): string {
  let file = "shield.png";
  if (fallbackOverride && fallbackOverride !== "false") {
    file = fallbackOverride === "white" ? "shield_white.png" : "shield_subtle_grey.png";
  }
  return `${CRICKET_TEAM_LOGO_S3_BASE}${CRICKET_TEAM_LOGO_DEFAULT_S3_FOLDER}/${file}`;
}

function legacyCdnLogoUrl(teamName: string): string {
  const name = String(teamName || "").trim();
  if (!name) return "";
  const folderSlug = name.replace(/\//g, " ").replace(/ /g, "+");
  const fileBase = name.replace(/\//g, "_").replace(/ /g, "_");
  const cdn = (process.env.CDN_ENDPOINT || DEFAULT_SPORT_IMAGES_CDN).replace(/\/$/, "");
  if (!folderSlug || !fileBase) return "";
  return `${cdn}/${LEGACY_CDN_PREFIX}/${folderSlug}/${fileBase}.png`;
}

export function cricketTeamLogoS3Url(teamName: string): string {
  const slug = slugifyCricketTeamName(teamName);
  if (!slug) return "";
  return `${CRICKET_TEAM_LOGO_S3_BASE}${slug}.png`;
}

/**
 * Ordered URLs to try for a team (flat S3 first, then legacy CDN), deduped.
 * Includes U19-normalized, parent, and Women suffix variants (aligned with backend lookup).
 */
export function getOrderedCricketTeamLogoUrls(teamName: string): string[] {
  const raw = String(teamName || "").trim();
  if (!raw) return [];

  const u19Normalized =
    raw
      .replace(/\s+under[- ]?19s?\s*/gi, " U19 ")
      .replace(/\s+/g, " ")
      .trim() || raw;

  const fullyNormalized =
    u19Normalized
      .replace(/\s*(?:women'?s?|u19|u-19)\s*$/i, "")
      .replace(/\s*(?:women'?s?|u19|u-19)\s+/gi, " ")
      .replace(/\s+/g, " ")
      .trim() || u19Normalized;

  const withWomen =
    fullyNormalized && !/\bwomen'?s?\b/i.test(fullyNormalized)
      ? `${fullyNormalized} Women`
      : "";

  const names = [raw, u19Normalized, fullyNormalized, withWomen].filter(Boolean);
  const urls: string[] = [];
  const seen = new Set<string>();
  const push = (url: string) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    urls.push(url);
  };

  for (const name of names) {
    push(cricketTeamLogoS3Url(name));
  }
  for (const name of names) {
    push(legacyCdnLogoUrl(name));
  }
  return urls;
}
